import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useConfig } from "../ConfigProvider";
import { useTunnelData } from "../TunnelContext";
import { useAnalytics } from "../analytics";
import Button from "../Button";
import StepHeader from "../StepHeader";
import Step from "./Step";
import Choice from "../Choice";
import PromoBanner from "../components/PromoBanner";

import "./Plans.scss";

export default function OfferSelect({ plans, goNext, errorMsg, errorMsgSet }) {
  const config = useConfig();
  const { tunnel, setTunnelOffer } = useTunnelData();
  const { trackOfferSelected } = useAnalytics();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const getInitialFilterTypeTabFromUrl = () => {
    const typeParam = searchParams.get("type");

    const PLAN_TYPES = ["subscription", "counter"]; // récupéré de resamania et stocké en db
    let SUBSCRIPTION_TYPES = [];
    if (config.subscriptionTypes) {
      SUBSCRIPTION_TYPES = config.subscriptionTypes.map(
        (subType) => subType.value,
      );
    }

    const ALLOWED_PARAMS = [...PLAN_TYPES, ...SUBSCRIPTION_TYPES];
    if (ALLOWED_PARAMS.includes(typeParam)) return typeParam;

    if (!config.plans.splitCounterAbo) return "*";

    // Quand des types d'abonnement sont configurés en BO (ex: "abo", "class"),
    // on ouvre sur le premier type pour que l'onglet soit sélectionné et les
    // formules affichées, au lieu d'une valeur "subscription" qui ne matche aucun onglet.
    const sortedSubscriptionTypes = [...(config.subscriptionTypes || [])].sort(
      (a, b) => (a.position || 0) - (b.position || 0),
    );
    const firstSubscriptionType = sortedSubscriptionTypes[0]?.value ?? "subscription";

    if (!tunnel.offer) return firstSubscriptionType;
    if (tunnel.offer.type === "counter" || !sortedSubscriptionTypes.length) {
      return tunnel.offer.type;
    }
    return tunnel.offer.vel_datas?.subscriptionTypeValue ?? "subscription";
  };

  const [selectedTypeFilterTab, setSelectedTypeFilterTab] = useState(
    getInitialFilterTypeTabFromUrl(),
  );
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const select = (offer) => {
    errorMsgSet(false);
    setTunnelOffer(offer);
    trackOfferSelected(offer);
    if (config.plans.autoSubmit || config.plans.buttonTitle) {
      goNext();
    }
  };

  const filterPlans = (plans, filterType) => {
    if (filterType === "*") {
      return plans;
    }

    if (filterType === "counter") {
      return plans.filter((o) => o.type === "counter");
    }

    if (filterType === "subscription") {
      return plans.filter((o) => o.type === "subscription");
    }

    return plans.filter(
      (o) => o.vel_datas.subscriptionTypeValue === filterType,
    );
  };

  let className = "OptionsList";
  if (config.options.desktopInlineBy3) {
    className += " desktopInlineBy3";
  }

  // Use the imageBaseURL from the tunnel club if available, otherwise use the config default
  let imageBaseURL = config.regulars.imageBaseURL;
  if (tunnel.club?.imageBaseURL) {
    imageBaseURL = tunnel.club.imageBaseURL;
  }

  return (
    <Step classe="Plans" step="Formules d'abonnement & options">
      <PromoBanner club={tunnel.club} />
      {config.plans.displayCompanyAndClubName && (
        <div className="Name">
        <span className="Company">{config.companyName}</span>
        <span className="Club">{tunnel.club.name}</span>
      </div>
      )}
      <header>
        <StepHeader text={config.plans.title} />
      </header>
      {selectedTypeFilterTab !== "*" && (
        <div className="typeFilterContainer">
          <div className="typeFilter">
            {config.plans.splitCounterAbo.subsText && !config.subscriptionTypes?.length && (
              <Button
                variant={
                  selectedTypeFilterTab === "subscription" ? "primary" : "textOnly"
                }
                className={
                  selectedTypeFilterTab === "subscription"
                    ? "selected"
                    : "not-selected"
                }
                onClick={() => setSelectedTypeFilterTab("subscription")}
                style={{
                  flexDirection: "column",
                  textAlign: "center",
                  padding: "1rem",
                }}
              >
                {config.plans.splitCounterAbo.subsText.map((t, i) => (
                  <span key={i}>{t}</span>
                ))}
              </Button>
            )}

            {config?.subscriptionTypes &&
              config.subscriptionTypes
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((subType) => (
                  <Button
                    key={subType.id}
                    variant={
                      selectedTypeFilterTab === subType.value
                        ? "primary"
                        : "textOnly"
                    }
                    className={
                      selectedTypeFilterTab === subType.value
                        ? "selected"
                        : "not-selected"
                    }
                    onClick={() => setSelectedTypeFilterTab(subType.value)}
                    style={{
                      flexDirection: "column",
                      textAlign: "center",
                      padding: "1rem",
                    }}
                  >
                    {subType.label}
                  </Button>
                ))}

            {config.plans.splitCounterAbo.passText && (
              <Button
                variant={
                  selectedTypeFilterTab === "counter" ? "primary" : "textOnly"
                }
                className={
                  selectedTypeFilterTab === "counter"
                    ? "selected"
                    : "not-selected"
                }
                onClick={() => setSelectedTypeFilterTab("counter")}
                style={{
                  flexDirection: "column",
                  textAlign: "center",
                  padding: "1rem",
                }}
              >
                {config.plans.splitCounterAbo.passText.map((t, i) => (
                  <span key={i}>{t}</span>
                ))}
              </Button>
            )}
          </div>
        </div>
      )}
      <ul className={className}>
        {filterPlans(plans, selectedTypeFilterTab).map((option, i) => {
          return (
            <li key={`option_${i}`} className="OptionsList-element">
              {option.topOffer ? (
                <div className="TopOffer">
                  {config.plans.topOfferTitle || "Populaire"}
                </div>
              ) : (
                <div className="spacerTopOfferEmpty"></div>
              )}
              <Choice
                value={option}
                selected={[tunnel.offer].filter((e) => e)}
                onSelect={select}
                useImage={config.plans.useImg}
                imageBaseURL={imageBaseURL}
                choiceType="offers"
              />
            </li>
          );
        })}
      </ul>
      <div className="OfferSelectFooterContainer">
        <div className="OfferSelectFooter">
          {!config.plans.buttonTitle && !tunnel.offer && (
            <span style={{ marginBottom: "0.5rem" }}>
              {config.plans.incitation}
            </span>
          )}
          {!config.plans.buttonTitle &&
            tunnel.offer &&
            !!tunnel.offer.offer.registrationFeeTI && (
              <>
                <span
                  style={{
                    marginBottom: "0.5rem",
                    flexDirection: "row",
                    gap: "1rem",
                  }}
                >
                  {config.plans.feesLabel}:
                  <strong>
                    {(tunnel.offer.offer.registrationFeeTI / 100).toFixed(2)} €
                  </strong>
                </span>
                {config.plans.feesLabelComplement &&
                  config.plans.feesLabelComplement !== "" && (
                    <span
                      style={{
                        marginBottom: "0.5rem",
                        paddingTop: "0",
                        flexDirection: "row",
                        gap: "1rem",
                      }}
                    >
                      {config.plans.feesLabelComplement}
                    </span>
                  )}
              </>
            )}
          {!config.plans.buttonTitle &&
            tunnel.offer &&
            !tunnel.offer.offer.registrationFeeTI && (
              <span style={{ marginBottom: "0.5rem" }}>
                {config.plans.nofeesLabel}
              </span>
            )}

          {!!errorMsg && (
            <p style={{ textAlign: "center", fontWeight: "bold" }}>
              {errorMsg}
            </p>
          )}
          {!config.plans.buttonTitle && (
            <Button
              className="NextButton"
              variant="primary"
              disabled={!tunnel.offer}
              onClick={goNext}
            >
              {config.plans.nextLabel}
            </Button>
          )}

          {(tunnel.club?.legalContent ?? config.defaultLegalContent) && (
            <p className="legalParagraph">
              {(tunnel.club?.legalContent ?? config.defaultLegalContent)
                .split("\n")
                .map((line, idx, arr) => (
                  <span key={idx}>
                    {line}
                    {idx < arr.length - 1 && <br />}
                  </span>
                ))}
            </p>
          )}
        </div>
      </div>
    </Step>
  );
}
